import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useSpring,
  useVelocity,
  wrap,
  useTransform,
  useScroll,
} from "motion/react";
import { useRef, useState, useEffect } from "react";

interface MarqueeBannerProps {
  text?: string;
  items?: React.ReactNode[];
  reverse?: boolean;
}

export function MarqueeBanner({ text, items, reverse = false }: MarqueeBannerProps) {
  const parts = items || (text ? text.split(" • ") : []);
  const baseX = useMotionValue(0);

  // Scroll velocity hooking
  const { scrollY } = useScroll();
  const scrollVelocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400,
  });

  const momentum = useMotionValue(0);
  const smoothMomentum = useSpring(momentum, {
    damping: 50,
    stiffness: 400,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const isInView = useRef(false);

  // Base speed: pixels per second
  const basePixelSpeed = 60;
  const directionFactor = reverse ? 1 : -1;
  const speed = basePixelSpeed * directionFactor;

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      isInView.current = true;
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        isInView.current = Boolean(entries[0]?.isIntersecting);
      },
      { rootMargin: '200px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useAnimationFrame((t, delta) => {
    if (!isInView.current || isDragging.current) return;

    const dt = delta / 1000;

    // Slingshot effect: add drag momentum AND scroll velocity
    // Take the absolute value so scrolling up OR down pushes it faster in the *default* direction.
    // Boosted multiplier to make it more noticeable.
    const scrollEffect = Math.abs(smoothVelocity.get()) * 0.4;

    // Normal base speed + dragging slingshot + scroll velocity injection
    const moveBy =
      (speed + smoothMomentum.get() + scrollEffect * directionFactor) * dt;

    baseX.set(baseX.get() + moveBy);

    // Decay drag momentum to 0 over time
    momentum.set(0);
  });

  const [cachedWidth, setCachedWidth] = useState(0);
  const [cachedGap, setCachedGap] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 28 : 32
  );

  useEffect(() => {
    if (!contentRef.current) return;
    
    // Use ResizeObserver entry.contentRect.width directly without querying window.innerWidth inside the callback
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === contentRef.current) {
          setCachedWidth(entry.contentRect.width);
        }
      }
    });
    
    observer.observe(contentRef.current);
    
    const handleResize = () => setCachedGap(window.innerWidth < 768 ? 28 : 32);
    window.addEventListener('resize', handleResize, { passive: true });
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [items, text]);

  // Wrap the X value based on content width
  const wrappedX = useMotionValue(0);
  useAnimationFrame(() => {
    if (!isInView.current) return;
    if (cachedWidth > 0) {
      wrappedX.set(wrap(-(cachedWidth + cachedGap), 0, baseX.get()));
    }
  });

  const xPos = useTransform(wrappedX, (v) => `${v}px`);

  const contentElements = [...Array(items ? 1 : 10)].flatMap((_, i) => [
    <span key={`bullet-green-${i}`} className="text-[#9af104] shrink-0" aria-hidden={i > 0 ? "true" : undefined}>
      •
    </span>,
    ...parts.flatMap((part, index) => {
      const isLast = index === parts.length - 1;
      const partNode = (
        <span key={`part-${i}-${index}`} className="whitespace-nowrap shrink-0" aria-hidden={i > 0 ? "true" : undefined}>
          {part}
        </span>
      );
      if (isLast) return [partNode];
      return [
        partNode,
        <span
          key={`bullet-black-${i}-${index}`}
          className="opacity-25 text-black shrink-0"
          aria-hidden={i > 0 ? "true" : undefined}
        >
          •
        </span>,
      ];
    }),
  ]);

  const repeatedTextNode = (
    <div
      ref={contentRef}
      className="flex gap-[28px] md:gap-8 pointer-events-none items-center h-full"
    >
      {contentElements}
    </div>
  );

  const repeatedTextNodeClone = (
    <div className="flex gap-[28px] md:gap-8 pointer-events-none items-center h-full" aria-hidden="true">
      {contentElements}
    </div>
  );

  return (
    <div ref={wrapperRef} data-nosnippet className="overflow-hidden whitespace-nowrap flex border-y border-black/20 py-3 md:py-[6px] bg-white text-black select-none touch-none cursor-none w-full hover-target">
      <motion.div
        ref={containerRef}
        className="flex gap-[28px] md:gap-8 w-max text-[26px] md:text-[36px] lg:text-[50px] font-bold uppercase tracking-tighter font-display leading-none md:leading-normal"
        style={{ x: xPos }}
        onPointerDown={() => {
          isDragging.current = true;
        }}
        onPointerUp={() => {
          isDragging.current = false;
        }}
        onPointerCancel={() => {
          isDragging.current = false;
        }}
        onPanStart={() => {
          isDragging.current = true;
        }}
        onPanEnd={() => {
          isDragging.current = false;
        }}
        onPan={(e, info) => {
          baseX.set(baseX.get() + info.delta.x);
          momentum.set(info.velocity.x);
        }}
      >
        {repeatedTextNode}
        {repeatedTextNodeClone}
      </motion.div>
    </div>
  );
}
